// The pages around the signer's view: the customer's link (/sign/:token, no sign-in), countersigning in courageMFG
// (/contracts/:id/sign), and the public verification page (/verify).
import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mark } from "../ui";
import { publicSign, sgApi, openPdf, sha256Hex, verify, ROLE_NAME, type SignerView, type VerifyResult } from "./sgApi";
import { SignDocument } from "./SignDocument";
import "./signing.css";

const verifyHref = () => `${location.origin}/verify`;
const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");

/** The customer's page, from the emailed link. No account, no sign-in: the link is theirs. */
export function PublicSignPage() {
  const { token = "" } = useParams();
  const [view, setView] = useState<SignerView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { document.title = "Review and sign"; publicSign.view(token).then(setView, (e) => setErr(e.message)); }, [token]);
  return (
    <div className="sg-public">
      <header className="sg-top">
        <Mark size={22} />
        <span className="sg-co">{view?.request.companyName ?? "courageMFG"}</span>
        <span className="sg-sp" />
        <span className="sg-sub">{view ? `Contract ${view.request.number}` : "Review and sign"}</span>
      </header>
      <main className="sg-main">
        {err ? <div className="sg-card warn"><h2>{err}</h2><p>Ask whoever sent you the contract for a new link.</p></div>
          : !view ? <div className="sg-card">Loading…</div>
          : <SignDocument view={view} onChange={setView} verifyHref={verifyHref()}
              pdf={publicSign.file} openDoc={(url, name, download) => (download ? openPdf(() => publicSign.file(url), name, true) : window.open(url, "_blank"))}
              consent={() => publicSign.consent(token).then(() => {})} sign={(b) => publicSign.sign(token, b)} decline={(reason) => publicSign.decline(token, reason)} />}
      </main>
      <footer className="sg-foot">Sent with courageMFG e-signature{view ? ` for ${view.recipient.name} (${view.recipient.email})` : ""}. Only open links sent to you.</footer>
    </div>
  );
}

/** The countersigner signs in courageMFG, on the same page the customer used. */
export function ContractSignPage() {
  const { id = "" } = useParams();
  const [view, setView] = useState<SignerView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { sgApi.signView(id).then(setView, (e) => setErr(e.message)); }, [id]);
  const fileOf = (url: string) => { const [, contract, , , fileId] = url.match(/\/api\/contracts\/([^/]+)\/(signing)\/(files)\/(.+)$/) ?? []; return sgApi.file(decodeURIComponent(contract ?? id), fileId ?? ""); };
  return (
    <div className="page card-page">
      <div className="rec-title"><h1>{view ? `Sign ${view.request.number}` : "Sign"}</h1><span className="meta"><Link to={`/contracts/${id}`}>Back to the contract</Link></span></div>
      <div className="sg-main" style={{ padding: 0, width: "min(880px, 100%)", margin: 0 }}>
        {err ? <div className="sg-card warn"><h2>{err}</h2><p><Link to={`/contracts/${id}`}>Back to the contract</Link></p></div>
          : !view ? <div className="sg-card">Loading…</div>
          : <SignDocument view={view} onChange={setView} verifyHref={verifyHref()}
              pdf={fileOf} openDoc={(url, name, download) => openPdf(() => fileOf(url), name, download)}
              consent={async () => {}} sign={(b) => sgApi.countersign(id, b)}
              after={<div className="sg-row"><Link className="btn primary" to={`/contracts/${id}`}>Back to the Contract</Link></div>} />}
      </div>
    </div>
  );
}

/** Anyone can check a PDF against what courageMFG signed. The file stays in the browser; only its fingerprint is sent. */
export function VerifyPage() {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<{ name: string; sha: string; result: VerifyResult } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { document.title = "Check a signed document"; }, []);
  const check = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true); setErr(null); setRes(null);
    try { const sha = await sha256Hex(f); setRes({ name: f.name, sha, result: await verify(sha) }); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  const r = res?.result;
  return (
    <div className="sg-public">
      <header className="sg-top"><Mark size={22} /><span className="sg-co">courageMFG</span><span className="sg-sp" /><span className="sg-sub">Check a signed document</span></header>
      <main className="sg-main" style={{ paddingBottom: 32 }}>
        <div className="sg-card">
          <h1>Check a signed document</h1>
          <p>Choose a PDF signed with courageMFG. Its fingerprint (SHA-256) is calculated on this device and compared with the one recorded when it was signed. The file itself is not uploaded.</p>
          <div className={`sg-drop ${over ? "over" : ""}`} style={{ marginTop: 14 }} role="button" tabIndex={0}
            onClick={() => input.current?.click()} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && input.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); check(e.dataTransfer.files[0]); }}>
            <strong>{busy ? "Checking…" : "Drop a PDF here, or choose one"}</strong>
            <span className="muted">The signed contract or its certificate of completion</span>
            <input ref={input} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => check(e.target.files?.[0] ?? undefined)} />
          </div>
        </div>
        {err && <div className="msg error" role="alert">{err}</div>}
        {res && r && (r.match ? (
          <div className={`sg-card ${r.kind === "original" ? "warn" : "ok"}`} role="status">
            <h2>{r.kind === "signed" ? "This is the signed document, unchanged" : r.kind === "certificate" ? "This is the certificate of completion, unchanged" : "This is the document as sent, before signing"}</h2>
            {r.newer && <p>{r.newer}</p>}
            <dl className="sg-dl">
              <dt>Document</dt><dd>{r.title}, contract {r.number}{r.revision > 1 ? `, revision ${r.revision}` : ""}</dd>
              <dt>From</dt><dd>{r.company}</dd>
              <dt>Status</dt><dd>{r.status === "completed" ? `Signed by everyone ${day(r.completedAt)}` : r.status === "out" ? "Out for signature" : r.status}</dd>
              {r.signers.map((s, i) => <Fragment key={i}><dt>{ROLE_NAME[s.role]}</dt><dd>{s.name}{s.signedAt ? `, signed ${day(s.signedAt)}` : ", not signed"}</dd></Fragment>)}
              <dt>Fingerprint</dt><dd className="sg-sha">{res.sha}</dd>
            </dl>
          </div>
        ) : (
          <div className="sg-card warn" role="status">
            <h2>No match</h2>
            <p>{res.name} doesn't match any document courageMFG signed. It may have been changed after signing, or it's a different file (a scan or a printed copy has a different fingerprint). Ask the sender for the original PDF.</p>
            <p className="sg-sha">{res.sha}</p>
          </div>
        ))}
      </main>
    </div>
  );
}
